import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { companyInfo } from '../mock';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const Contact = () => {
  const { language } = useLanguage();
  const resetTimerRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Show success message
    setIsSubmitted(true);
    toast.success(language === 'id'
      ? 'Pesan Anda berhasil dikirim! Tim kami akan segera menghubungi Anda.'
      : 'Your message has been sent successfully! Our team will contact you shortly.'
    );

    // Reset form after 3 seconds
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setIsSubmitted(false);
      resetTimerRef.current = null;
    }, 3000);
  };

  const handleWhatsApp = () => {
    const phone = companyInfo.phone[0].replace(/[^0-9]/g, '');
    const message = encodeURIComponent(t(language, 'contact.whatsappMessage'));
    const url = `https://wa.me/${phone}?text=${message}`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    } else {
      toast.error(language === 'id'
        ? 'Popup diblokir browser. Izinkan popup untuk membuka WhatsApp.'
        : 'Popup blocked by browser. Allow popups to open WhatsApp.'
      );
    }
  };

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-[1.15]">
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent pb-2">
              {language === 'id'
                ? 'Temukan Solusi IT Bisnis Anda!'
                : 'Find Your Business IT Solution Now!'
              }
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'id'
              ? 'Tim expert kami siap membantu Anda menemukan solusi IT terbaik untuk bisnis Anda'
              : 'Our expert team is ready to help you find the best IT solutions for your business'
            }
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Form */}
          <div className="w-full">
            <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl border border-gray-100">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {language === 'id' ? 'Nama Lengkap *' : 'Full Name *'}
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'id' ? 'Masukkan nama Anda' : 'Enter your name'}
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="name@company.com"
                        required
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {language === 'id' ? 'Nomor Telepon *' : 'Phone Number *'}
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="08xxxxxxxxxx"
                        required
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {language === 'id' ? 'Nama Perusahaan' : 'Company Name'}
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={language === 'id' ? 'Nama perusahaan Anda' : 'Your company name'}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'id' ? 'Pesan *' : 'Message *'}
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={language === 'id' ? 'Ceritakan kebutuhan IT Anda...' : 'Tell us about your IT needs...'}
                      rows={5}
                      required
                      className="w-full resize-none"
                    />
                  </div>

                  {/* Action Button - Send Message Only */}
                  <Button
                    type="submit"
                    className="w-full text-white py-6 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center hover:brightness-110"
                    style={{ background: 'linear-gradient(56deg, rgba(13, 41, 67, 1) 0%, rgba(25, 54, 112, 1) 100%)' }}
                  >
                    <Send className="mr-2" size={20} />
                    {language === 'id' ? 'Kirim Pesan' : 'Send Message'}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="text-green-600" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {language === 'id' ? 'Pesan Terkirim!' : 'Message Sent!'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'id'
                      ? 'Tim kami akan segera menghubungi Anda. Terima kasih!'
                      : 'Our team will contact you shortly. Thank you!'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;


